import './Pagination.scss';
import classnames from 'classnames';
import { FormattedMessage } from 'components/shared/Intl';
import { ChangeEvent, FC, Fragment, MouseEvent, useCallback, useMemo } from 'react';

import { GenericErrorBoundary } from '../../../common-form/builder/form/GenericErrorBoundary';
import { CreditCheckBadge } from '../CreditCheckBadge/CreditCheckBadge.connect';
import type { PaginationLink } from './Pagination.helper';
import {
  generatePaginationLinkDataE2E,
  generatePaginationLinks,
  isCurrentPageLink,
  isFirstPageLink,
  isLastPageLink,
  isNextPageLink,
  isPreviousPageLink,
  isSeparatorLink,
  isSiblingPageLink,
} from './Pagination.helper';
import type { PaginationDispatchProps, PaginationStateProps } from './Pagination.model';
import { PaginationLayout } from './PaginationLayout';
import { PaginationSummaryEntries } from './PaginationSummaryEntries';

export const defaultAvailableItemsPerPage = [10, 25, 50, 100, 250];
export type PaginationProps = PaginationStateProps &
  PaginationDispatchProps & {
    className?: string;
    hasSummary?: boolean;
    availableItemsPerPage?: number[];
    siblingsMaxCnt?: number;
    showEntriesLabelVisible?: boolean;
    hideOnCurrentItemsLowerThanPageSize?: boolean;
  };

export const Pagination: FC<PaginationProps> = ({
  className,
  page = 0,
  itemsPerPage,
  totalItems = 0,
  isSmall,
  hasSummary = true,
  shouldShowCreditCheckBadge = false,
  onPageChanged,
  onSizeChanged,
  availableItemsPerPage,
  siblingsMaxCnt = 2,
  showEntriesLabelVisible = true,
  onPaginationChanged,
  totalPages,
  showOnlySummary,
  hideOnCurrentItemsLowerThanPageSize = false,
}) => {
  const changePage = useCallback(
    (page: number, e?: MouseEvent<any>) => {
      e?.preventDefault();
      onPageChanged?.(page);
    },
    [onPageChanged],
  );
  const computedAvailableItemsPerPage = useMemo<number[]>(
    () => availableItemsPerPage ?? defaultAvailableItemsPerPage,
    [availableItemsPerPage],
  );
  const size = useMemo(
    () => Math.min(250, itemsPerPage ?? computedAvailableItemsPerPage[0]),
    [itemsPerPage, computedAvailableItemsPerPage],
  );
  const pages = useMemo(
    () => totalPages ?? (size > 0 ? Math.ceil(totalItems / size) : 0),
    [totalItems, totalPages, size],
  );

  const changeItemsPerPage = useCallback(
    (event: ChangeEvent<HTMLSelectElement>) => {
      const selectedSize = parseInt(event.target.value, 10);
      const isOutOfTotalItemsBound = selectedSize * (page - 1) + selectedSize > totalItems;
      if (isOutOfTotalItemsBound) {
        onPaginationChanged?.(1, selectedSize);
      }
      onSizeChanged?.(selectedSize);
    },
    [onSizeChanged, onPaginationChanged, page, totalItems],
  );
  if (hideOnCurrentItemsLowerThanPageSize && page === 1 && totalItems < page * size) {
    return null;
  }
  return (
    <GenericErrorBoundary>
      <PaginationLayout className={className} isSmall={isSmall}>
        <div className="d-none d-md-flex align-self-center me-auto ps-2">
          {hasSummary && totalItems !== 0 && (
            <PaginationSummaryEntries page={page} itemsPerPage={itemsPerPage} totalItems={totalItems} />
          )}
        </div>
        <div className="flex-grow-1 align-self-center ms-auto ps-1">
          {shouldShowCreditCheckBadge && <CreditCheckBadge />}
        </div>

        {!showOnlySummary && (
          <>
            {page !== 0 && itemsPerPage !== 0 && itemsPerPage !== 0 && onSizeChanged && (
              <div className="page-selector d-none d-lg-flex me-2 gap-2">
                <span className="align-self-center me-2">
                  <FormattedMessage id="blotter.pagination.show" />
                </span>
                <select
                  className="form-select me-2"
                  data-e2e="items-per-page"
                  name="itemsPerPage"
                  onChange={changeItemsPerPage}
                  value={size}
                >
                  {computedAvailableItemsPerPage.map(value => (
                    <option label={value.toString()} key={value} value={`${value}`} data-e2e="items-per-page-option">
                      {value}
                    </option>
                  ))}
                </select>
                {showEntriesLabelVisible && (
                  <span className="align-self-center">
                    <FormattedMessage id="blotter.pagination.entries" />
                  </span>
                )}
              </div>
            )}

            <nav className="align-self-center" aria-label="Page navigation">
              <ul className="pagination pagination-borderless m-0">
                {generatePaginationLinks(page, pages, siblingsMaxCnt).map((link: PaginationLink, _index: number) => (
                  <li
                    key={_index}
                    className={classnames('page-item', { active: isCurrentPageLink(link) })}
                    data-e2e={generatePaginationLinkDataE2E(link)}
                  >
                    <a className="page-link" onClick={e => changePage(link.page, e)} data-e2e="pagination-link">
                      {isFirstPageLink(link) && <i className="icon icon-xs">first_page</i>}
                      {isPreviousPageLink(link) && <i className="icon icon-xs">chevron_left</i>}
                      {isNextPageLink(link) && <i className="icon icon-xs">chevron_right</i>}
                      {isLastPageLink(link) && <i className="icon icon-xs">last_page</i>}
                      {isSeparatorLink(link) && <i className="icon icon-xs">more_horiz</i>}
                      {(isSiblingPageLink(link) || isCurrentPageLink(link)) && <Fragment>{link.page}</Fragment>}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
          </>
        )}
      </PaginationLayout>
    </GenericErrorBoundary>
  );
};

Pagination.displayName = 'Pagination';
